/**
 * Quadro branco partilhado da chamada.
 *
 * Um "quadro mesmo": uma superfície branca por cima do palco onde toda a gente
 * na call desenha ao mesmo tempo — pincel, tamanho, borracha, texto e cor.
 *
 * Três decisões que sustentam isto:
 *
 *  1. SEM BACKEND. Viaja tudo pelo canal de dados do LiveKit (`voiceEngine`),
 *     o mesmo SFU que já leva a voz. O quadro é efémero: existe enquanto a call
 *     existe, e não toca na base de dados. Zero rotas novas na API.
 *  2. OPERAÇÕES, NÃO PÍXEIS. O estado é uma lista de traços/textos com um `id`.
 *     Aplicar é idempotente (dedup por id), por isso reenviar é seguro e quem
 *     entra a meio pede um "sync" e recebe a lista inteira — sem servidor a
 *     guardar nada. A verdade viaja em pacotes *reliable*; o arrasto ao vivo em
 *     pacotes *lossy* (mais rápidos) que um commit reliable no fim cura.
 *  3. CANVAS IMPERATIVO, FORA DO REACT. Redesenhar a 60fps não pode passar por
 *     `setState` — os traços vivem em refs e o canvas é repintado num rAF. O
 *     React só trata da barra de ferramentas (ferramenta, cor, tamanho).
 */

import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { voiceEngine } from "../lib/voice";
import { IconBrush, IconClose, IconEraser, IconText, IconTrash, IconUndo } from "./Icons";

type Tool = "brush" | "eraser" | "text";

/** Um traço: pontos em fracções 0..1 do quadro (`[x0,y0,x1,y1,…]`), para
 *  sobreviver a um redimensionamento sem sair do sítio. `erase` pinta com
 *  `destination-out` — apaga o que está por baixo em vez de acrescentar tinta. */
interface Stroke {
  k: "stroke";
  id: string;
  by: string;
  color: string;
  size: number;
  erase: boolean;
  pts: number[];
}

interface TextOp {
  k: "text";
  id: string;
  by: string;
  color: string;
  size: number;
  x: number;
  y: number;
  text: string;
}

type Op = Stroke | TextOp;

/** Mensagens do fio. Commits (`stroke`/`text`/`remove`/`clear`) são reliable;
 *  `live` é o preview lossy do arrasto; `hello`/`sync` fazem o catch-up de quem
 *  chega depois. */
type Msg =
  | Op
  | { k: "live"; id: string; by: string; color: string; size: number; erase: boolean; seg: number[] }
  | { k: "remove"; id: string }
  | { k: "clear" }
  | { k: "hello"; by: string }
  | { k: "sync"; to: string; ops: Op[] };

const COLORS = ["#111827", "#ef4444", "#f59e0b", "#22c55e", "#2563eb", "#a855f7"];
const MIN_SIZE = 2;
const MAX_SIZE = 40;
/** O texto acompanha o tamanho do pincel: pincel grande, letra grande. */
const TEXT_SCALE = 5;

function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/** Valida uma mensagem crua do canal de dados. Nada de `any` a entrar cru: uma
 *  call é território partilhado e um pacote malformado não pode rebentar o loop. */
function parse(data: unknown): Msg | null {
  if (typeof data !== "object" || data === null) return null;
  const m = data as Record<string, unknown>;
  switch (m["k"]) {
    case "stroke":
      return {
        k: "stroke",
        id: String(m["id"] ?? ""),
        by: String(m["by"] ?? ""),
        color: String(m["color"] ?? "#111827"),
        size: num(m["size"]),
        erase: Boolean(m["erase"]),
        pts: Array.isArray(m["pts"]) ? (m["pts"] as unknown[]).map(num) : [],
      };
    case "text":
      return {
        k: "text",
        id: String(m["id"] ?? ""),
        by: String(m["by"] ?? ""),
        color: String(m["color"] ?? "#111827"),
        size: num(m["size"]),
        x: num(m["x"]),
        y: num(m["y"]),
        text: String(m["text"] ?? ""),
      };
    case "live":
      return {
        k: "live",
        id: String(m["id"] ?? ""),
        by: String(m["by"] ?? ""),
        color: String(m["color"] ?? "#111827"),
        size: num(m["size"]),
        erase: Boolean(m["erase"]),
        seg: Array.isArray(m["seg"]) ? (m["seg"] as unknown[]).map(num) : [],
      };
    case "remove":
      return { k: "remove", id: String(m["id"] ?? "") };
    case "clear":
      return { k: "clear" };
    case "hello":
      return { k: "hello", by: String(m["by"] ?? "") };
    case "sync":
      return {
        k: "sync",
        to: String(m["to"] ?? ""),
        ops: Array.isArray(m["ops"])
          ? (m["ops"] as unknown[]).map(parse).filter((o): o is Op => o?.k === "stroke" || o?.k === "text")
          : [],
      };
    default:
      return null;
  }
}

export function Whiteboard({
  open,
  active,
  onClose,
}: {
  /** Visível? Fechado fica montado (a acumular operações) mas `display:none`. */
  open: boolean;
  /** Ligado à call? Só com identidade local se pode desenhar e pedir sync. */
  active: boolean;
  onClose: () => void;
}) {
  const [tool, setTool] = useState<Tool>("brush");
  const [color, setColor] = useState<string>(COLORS[1]!);
  const [size, setSize] = useState<number>(6);
  const [editing, setEditing] = useState<{ x: number; y: number } | null>(null);

  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  // Estado do desenho — em refs, nunca em state: repintar a 60fps não pode
  // disparar re-renders do React.
  const opsRef = useRef<Op[]>([]); // committed, por ordem de desenho
  const liveRef = useRef<Map<string, Stroke>>(new Map()); // arrastos em curso (meus e alheios)
  const activeRef = useRef<Stroke | null>(null); // o meu arrasto atual
  const mineRef = useRef<string[]>([]); // ids que criei — para o "desfazer"
  const seqRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const pendingSeg = useRef<number[]>([]);
  const rafLive = useRef<number | null>(null);

  // Refs-espelho do que os handlers precisam sem virar dependência de efeitos.
  const toolRef = useRef(tool);
  const colorRef = useRef(color);
  const sizeRef = useRef(size);
  toolRef.current = tool;
  colorRef.current = color;
  sizeRef.current = size;

  const nextId = () => `${voiceEngine.localId ?? "?"}:${(seqRef.current += 1)}`;

  // ── pintura ─────────────────────────────────────────────────

  const redraw = useCallback(() => {
    const cv = canvasRef.current;
    const ctx = cv?.getContext("2d");
    if (!cv || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.width;
    const H = cv.height;
    ctx.clearRect(0, 0, W, H);

    const drawStroke = (s: Stroke) => {
      if (s.pts.length < 2) return;
      ctx.globalCompositeOperation = s.erase ? "destination-out" : "source-over";
      ctx.strokeStyle = s.color;
      ctx.fillStyle = s.color;
      ctx.lineWidth = Math.max(1, s.size * dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (s.pts.length === 2) {
        // Um toque só: um ponto redondo, senão não se via nada.
        ctx.beginPath();
        ctx.arc(s.pts[0]! * W, s.pts[1]! * H, Math.max(0.5, (s.size * dpr) / 2), 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      ctx.beginPath();
      ctx.moveTo(s.pts[0]! * W, s.pts[1]! * H);
      for (let i = 2; i < s.pts.length; i += 2) ctx.lineTo(s.pts[i]! * W, s.pts[i + 1]! * H);
      ctx.stroke();
    };

    const drawText = (t: TextOp) => {
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = t.color;
      const px = Math.max(8, t.size * TEXT_SCALE * dpr);
      ctx.font = `${px}px ui-sans-serif, system-ui, sans-serif`;
      ctx.textBaseline = "top";
      const lines = t.text.split("\n");
      for (let i = 0; i < lines.length; i += 1) {
        ctx.fillText(lines[i]!, t.x * W, t.y * H + i * px * 1.25);
      }
    };

    for (const op of opsRef.current) op.k === "stroke" ? drawStroke(op) : drawText(op);
    for (const s of liveRef.current.values()) drawStroke(s);
    ctx.globalCompositeOperation = "source-over";
  }, []);

  const scheduleRedraw = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      redraw();
    });
  }, [redraw]);

  const resize = useCallback(() => {
    const cv = canvasRef.current;
    const host = hostRef.current;
    if (!cv || !host) return;
    const rect = host.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(rect.width * dpr);
    const h = Math.round(rect.height * dpr);
    if (cv.width !== w || cv.height !== h) {
      cv.width = w;
      cv.height = h;
    }
    redraw();
  }, [redraw]);

  // ── aplicar operações (locais e remotas) ────────────────────

  const applyOp = useCallback(
    (op: Op) => {
      if (!op.id) return;
      if (opsRef.current.some((o) => o.id === op.id)) return; // dedup — idempotente
      if (op.k === "stroke") liveRef.current.delete(op.id); // o commit substitui o preview
      opsRef.current.push(op);
      scheduleRedraw();
    },
    [scheduleRedraw],
  );

  const removeOp = useCallback(
    (id: string) => {
      const before = opsRef.current.length;
      opsRef.current = opsRef.current.filter((o) => o.id !== id);
      liveRef.current.delete(id);
      if (opsRef.current.length !== before) scheduleRedraw();
    },
    [scheduleRedraw],
  );

  // ── canal de dados ──────────────────────────────────────────

  useEffect(() => {
    const off = voiceEngine.subscribeData((from, data) => {
      const m = parse(data);
      if (!m) return;
      switch (m.k) {
        case "stroke":
        case "text":
          applyOp(m);
          break;
        case "live": {
          let s = liveRef.current.get(m.id);
          if (!s) {
            s = { k: "stroke", id: m.id, by: m.by || from, color: m.color, size: m.size, erase: m.erase, pts: [] };
            liveRef.current.set(m.id, s);
          }
          s.pts.push(...m.seg);
          scheduleRedraw();
          break;
        }
        case "remove":
          removeOp(m.id);
          break;
        case "clear":
          opsRef.current = [];
          liveRef.current.clear();
          mineRef.current = [];
          scheduleRedraw();
          break;
        case "hello":
          // Alguém entrou: se tenho estado, envio-o. Duplicados de vários
          // respondentes são inofensivos (o applyOp faz dedup por id).
          if (opsRef.current.length) voiceEngine.sendData({ k: "sync", to: m.by, ops: opsRef.current });
          break;
        case "sync":
          if (m.to === voiceEngine.localId) for (const op of m.ops) applyOp(op);
          break;
      }
    });
    return off;
  }, [applyOp, removeOp, scheduleRedraw]);

  // Ao ligar à call, pede o estado atual a quem já lá está.
  useEffect(() => {
    if (active) voiceEngine.sendData({ k: "hello", by: voiceEngine.localId ?? "" });
  }, [active]);

  // Redimensiona com o palco e ao abrir (o `display:none` mede 0).
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const ro = new ResizeObserver(() => resize());
    ro.observe(host);
    return () => ro.disconnect();
  }, [resize]);

  useEffect(() => {
    if (open) requestAnimationFrame(() => resize());
  }, [open, resize]);

  // ANTES da pintura, e não depois: focar a caixa num efeito passivo deixava-a
  // um frame à espera, e é nesse frame que um clique ainda a caminho lhe podia
  // tirar o foco. É a mesma avaria do `preventDefault` lá em baixo, vista do
  // outro lado — as duas fecham a mesma porta.
  useLayoutEffect(() => {
    if (editing) textRef.current?.focus();
  }, [editing]);

  // ── ponteiro ────────────────────────────────────────────────

  const normPoint = (e: ReactPointerEvent<HTMLCanvasElement>): [number, number] => {
    const cv = canvasRef.current!;
    const r = cv.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  };

  const flushLive = () => {
    if (rafLive.current != null) {
      cancelAnimationFrame(rafLive.current);
      rafLive.current = null;
    }
    pendingSeg.current = [];
  };

  const queueLive = (s: Stroke, nx: number, ny: number) => {
    pendingSeg.current.push(nx, ny);
    if (rafLive.current != null) return;
    rafLive.current = requestAnimationFrame(() => {
      rafLive.current = null;
      const seg = pendingSeg.current;
      pendingSeg.current = [];
      if (seg.length) {
        voiceEngine.sendData(
          { k: "live", id: s.id, by: s.by, color: s.color, size: s.size, erase: s.erase, seg },
          false,
        );
      }
    });
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!active) return;
    const [nx, ny] = normPoint(e);
    if (toolRef.current === "text") {
      /*
       * O `preventDefault` é a ferramenta de texto inteira.
       *
       * Sem ele acontece o que acontece a qualquer clique: a seguir ao
       * `pointerdown` o browser dispara o `mousedown` de compatibilidade, e a
       * acção por omissão desse é MEXER O FOCO — para o elemento focável mais
       * próximo, e a `<canvas>` não é um. O foco vai para o `body`.
       *
       * A caixa abria (o React trata o `setEditing` como evento discreto e corre
       * o efeito que a foca antes de devolver o controlo ao browser), levava com
       * o `mousedown` logo a seguir, perdia o foco, e o `onBlur` fechava-a com o
       * campo vazio. Do lado de cá do ecrã: carregar com a ferramenta de texto
       * não fazia rigorosamente nada.
       *
       * É o defeito clássico do editor-que-abre-ao-clique, e a razão de o balão
       * de fala do escritório nunca ter sofrido dele: aquele abre por TECLA.
       */
      e.preventDefault();
      setEditing({ x: nx, y: ny });
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    const s: Stroke = {
      k: "stroke",
      id: nextId(),
      by: voiceEngine.localId ?? "",
      color: colorRef.current,
      size: sizeRef.current,
      erase: toolRef.current === "eraser",
      pts: [nx, ny],
    };
    activeRef.current = s;
    liveRef.current.set(s.id, s);
    queueLive(s, nx, ny);
    scheduleRedraw();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const s = activeRef.current;
    if (!s) return;
    const [nx, ny] = normPoint(e);
    s.pts.push(nx, ny);
    queueLive(s, nx, ny);
    scheduleRedraw();
  };

  const endStroke = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const s = activeRef.current;
    if (!s) return;
    activeRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* o capture pode já ter saído */
    }
    flushLive();
    liveRef.current.delete(s.id);
    applyOp(s); // vira commit local
    mineRef.current.push(s.id);
    voiceEngine.sendData(s); // reliable — a verdade que cura o preview lossy
  };

  // ── texto ───────────────────────────────────────────────────

  const commitText = () => {
    const pos = editing;
    const value = textRef.current?.value.trim() ?? "";
    setEditing(null);
    if (!pos || !value) return;
    const op: TextOp = {
      k: "text",
      id: nextId(),
      by: voiceEngine.localId ?? "",
      color: colorRef.current,
      size: sizeRef.current,
      x: pos.x,
      y: pos.y,
      text: value,
    };
    applyOp(op);
    mineRef.current.push(op.id);
    voiceEngine.sendData(op);
  };

  // ── barra ───────────────────────────────────────────────────

  const undo = () => {
    const id = mineRef.current.pop();
    if (!id) return;
    removeOp(id);
    voiceEngine.sendData({ k: "remove", id });
  };

  const clear = () => {
    opsRef.current = [];
    liveRef.current.clear();
    mineRef.current = [];
    scheduleRedraw();
    voiceEngine.sendData({ k: "clear" });
  };

  const cursor = tool === "text" ? "text" : "crosshair";

  return (
    <div className={`board${open ? "" : " board--hidden"}`} ref={hostRef}>
      <canvas
        ref={canvasRef}
        className="board__canvas"
        style={{ cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
      />

      {editing ? (
        <textarea
          ref={textRef}
          className="board__text"
          style={{
            left: `${editing.x * 100}%`,
            top: `${editing.y * 100}%`,
            color: colorRef.current,
            fontSize: `${Math.max(10, size * TEXT_SCALE)}px`,
          }}
          onBlur={commitText}
          onKeyDown={(e) => {
            /*
             * A LINHA QUE FAZ ESCREVER-SE AQUI DENTRO.
             *
             * O Phaser do escritório ouve o teclado na WINDOW e faz
             * `preventDefault` a tudo o que captura — WASD, setas, E, T. O React
             * ouve na raiz da app, ou seja ANTES; parar aqui a propagação é o que
             * impede o jogo de engolir as letras. Sem isto escrevia-se tudo menos
             * as teclas do mundo, que são das mais comuns da língua: *"as teclas
             * que são utilizadas no office 2d não funcionam ao digitar no
             * quadro"*.
             *
             * Não substitui o `teclasOcupadas` do `OfficeSpace`, complementa-o: o
             * gate trata da tecla que já estava EM BAIXO quando a caixa abriu (o
             * jogo nunca vê o `keyup` dela e continuaria a empurrar o boneco);
             * isto trata das que se carregam com a caixa aberta.
             *
             * É a mesma linha, pela mesma razão, que o balão de fala já tinha —
             * ver `SayComposer`, em `OfficePanels`.
             */
            e.stopPropagation();
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              commitText();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setEditing(null);
            }
          }}
          rows={1}
          placeholder="Escreva…"
        />
      ) : null}

      <div className="board__bar" role="toolbar" aria-label="Ferramentas do quadro">
        <button
          type="button"
          className={`board__tool${tool === "brush" ? " is-active" : ""}`}
          onClick={() => setTool("brush")}
          title="Pincel"
        >
          <IconBrush size={16} />
        </button>
        <button
          type="button"
          className={`board__tool${tool === "eraser" ? " is-active" : ""}`}
          onClick={() => setTool("eraser")}
          title="Borracha"
        >
          <IconEraser size={16} />
        </button>
        <button
          type="button"
          className={`board__tool${tool === "text" ? " is-active" : ""}`}
          onClick={() => setTool("text")}
          title="Texto"
        >
          <IconText size={16} />
        </button>

        <span className="board__sep" aria-hidden="true" />

        <input
          type="range"
          className="board__size"
          min={MIN_SIZE}
          max={MAX_SIZE}
          value={size}
          onChange={(e) => setSize(Number(e.target.value))}
          title={`Tamanho: ${size}px`}
          aria-label="Tamanho do pincel"
        />
        <span className="board__dot" aria-hidden="true">
          <span style={{ width: size, height: size, background: tool === "eraser" ? "#cbd5e1" : color }} />
        </span>

        <span className="board__sep" aria-hidden="true" />

        <div className="board__swatches">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`board__swatch${color === c && tool !== "eraser" ? " is-active" : ""}`}
              style={{ background: c }}
              onClick={() => {
                setColor(c);
                if (tool === "eraser") setTool("brush");
              }}
              title={`Cor ${c}`}
              aria-label={`Cor ${c}`}
            />
          ))}
        </div>

        <span className="board__sep" aria-hidden="true" />

        <button type="button" className="board__tool" onClick={undo} title="Desfazer meu último">
          <IconUndo size={16} />
        </button>
        <button type="button" className="board__tool" onClick={clear} title="Limpar tudo">
          <IconTrash size={16} />
        </button>
        <button type="button" className="board__tool board__tool--close" onClick={onClose} title="Fechar quadro">
          <IconClose size={16} />
        </button>
      </div>
    </div>
  );
}
